import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { createConsumer } from '@rails/actioncable';
import { Send, X } from 'lucide-react';

// Generic 1:1 DM modal. Keyed by conversationId — the caller is responsible
// for ensuring the Conversation exists (e.g. via start_team_dm). Hydrates
// thread history on mount, subscribes to ChatChannel for live updates, and
// posts via send_message.
//
// Distinct from AdvisoryMessageModal (which is keyed by AdvisoryTeamMember
// and handles the off-platform email-fallback branch). DMs here are always
// in-app between platform users.

const CONVERSATION_MESSAGES_QUERY = gql`
  query ConversationMessages($id: ID!) {
    conversation_messages(conversation_id: $id)
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($cid: ID!, $body: String!) {
    send_message(conversation_id: $cid, body: $body) {
      message
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_e) {
    return '';
  }
}

export default function DirectMessageModal({
  isOpen,
  onClose,
  conversationId,
  recipientName,
  recipientSubtitle,
}) {
  const [body, setBody] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [liveMessages, setLiveMessages] = useState([]);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);
  const consumerRef = useRef(null);
  const subscriptionRef = useRef(null);

  const { data, loading, refetch } = useQuery(CONVERSATION_MESSAGES_QUERY, {
    variables: { id: conversationId },
    skip: !isOpen || !conversationId,
    fetchPolicy: 'cache-and-network',
  });

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE);

  const persistedMessages = data?.conversation_messages || [];

  // Merge persisted history with cable-arrived live messages, dedup by id.
  const messages = useMemo(() => {
    const byId = new Map();
    [...persistedMessages, ...liveMessages].forEach((m) => {
      byId.set(String(m.id), m);
    });
    return Array.from(byId.values());
  }, [persistedMessages, liveMessages]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reset compose state on open.
  useEffect(() => {
    if (isOpen) {
      setBody('');
      setSubmitError('');
      setLiveMessages([]);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen, conversationId]);

  // Subscribe to ChatChannel when open. Re-subscribe on conversation change.
  useEffect(() => {
    if (!isOpen || !conversationId) return undefined;

    consumerRef.current = createConsumer();
    subscriptionRef.current = consumerRef.current.subscriptions.create(
      { channel: 'ChatChannel', room_id: conversationId },
      {
        received: (payload) => {
          if (!payload || payload.type !== 'message' || !payload.message) return;
          const incoming = {
            id: String(payload.message.id),
            body: payload.message.body || payload.message.content,
            sender_user_id: payload.message.sender_user_id,
            sent_by_me: false, // server doesn't know viewer; FE sets via refetch
            sent_at: payload.message.sent_at || payload.message.sent,
          };
          if (messagesRef.current.some((m) => String(m.id) === incoming.id)) return;
          // Trigger a refetch so sent_by_me is correct from the server's view.
          refetch();
        },
      },
    );

    return () => {
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
      if (consumerRef.current) consumerRef.current.disconnect();
      subscriptionRef.current = null;
      consumerRef.current = null;
    };
  }, [isOpen, conversationId, refetch]);

  // Scroll to bottom on new message arrival or open.
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isOpen]);

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending || !conversationId) return;
    setSubmitError('');
    try {
      const { data: result } = await sendMessage({ variables: { cid: conversationId, body: trimmed } });
      const errors = result?.send_message?.errors || [];
      if (errors.length) {
        setSubmitError(errors.join(', '));
        return;
      }
      setBody('');
      refetch();
      if (typeof window !== 'undefined' && window.ahoy?.track) {
        try {
          window.ahoy.track('succeedo_team_dm_message_sent', { conversation_id: conversationId });
        } catch (_e) { /* swallow */ }
      }
    } catch (e) {
      setSubmitError(e?.message || 'Could not send. Please try again.');
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[hsl(var(--background))]/80 backdrop-blur-sm"
        onClick={onClose}
        role="presentation"
      />

      <div className="relative bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg w-full max-w-[560px] max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)_auto] shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--succeedo-orange))]/15 text-[hsl(var(--succeedo-orange))] flex items-center justify-center font-medium text-[14px]">
              {deriveInitials(recipientName)}
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">
                {recipientName || 'Direct message'}
              </h2>
              {recipientSubtitle && (
                <div className="text-[12px] text-[hsl(var(--muted-foreground))]">
                  {recipientSubtitle}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Thread */}
        <div className="overflow-y-auto p-6 space-y-3 min-h-[200px]">
          {loading && messages.length === 0 && (
            <p className="text-[13px] text-[hsl(var(--muted-foreground))] text-center">Loading…</p>
          )}
          {!loading && messages.length === 0 && (
            <p className="text-[13px] text-[hsl(var(--muted-foreground))] text-center py-8">
              No messages yet. Send the first one below.
            </p>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.sent_by_me ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  m.sent_by_me
                    ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                }`}
              >
                <p className="text-[14px] whitespace-pre-line">{m.body}</p>
                <p className={`text-[10px] mt-1 ${m.sent_by_me ? 'text-white/70' : 'text-[hsl(var(--muted-foreground))]'}`}>
                  {formatTimestamp(m.sent_at)}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Compose */}
        <div className="border-t border-[hsl(var(--border))] p-4">
          {submitError && (
            <div className="mb-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-[12px] px-3 py-2">
              {submitError}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || !conversationId}
              placeholder="Type your message…  (⌘/Ctrl+Enter to send)"
              rows={3}
              className="flex-1 bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-lg px-3 py-2 text-[14px] text-[hsl(var(--foreground))] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none resize-none disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!body.trim() || sending || !conversationId}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
